import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAdminBooks, getActivity } from "@/lib/api";
import { BookOpen, FileText, Users2, Image, Newspaper, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;
    listAdminBooks()
      .then((data) => { if (!cancelled) setBooks(data); })
      .catch(() => { /* dashboard degrades gracefully */ });
    getActivity(10)
      .then((data) => { if (!cancelled) setActivity(data); })
      .catch(() => { /* activity is optional */ });
    return () => { cancelled = true; };
  }, []);

  const counts = {
    published: books.filter(b => b.publish_status === "published").length,
    draft: books.filter(b => b.publish_status === "draft").length,
    archived: books.filter(b => b.publish_status === "archived").length,
  };

  return (
    <div data-testid="admin-dashboard">
      <PageHeader eyebrow="Overview" title="Welcome back" note={`${user.name} · signed in as ${user.role}`} />

      <div className="grid gap-6 md:grid-cols-3 mt-10">
        <StatCard label="Published books" value={counts.published} testId="stat-published" tone="maroon" />
        <StatCard label="Drafts" value={counts.draft} testId="stat-draft" tone="gold" />
        <StatCard label="Archived" value={counts.archived} testId="stat-archived" tone="muted" />
      </div>

      <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr] mt-12">
        <section>
          <h3 className="font-accent text-[10px] mb-4" style={{ color: "var(--gi-maroon)" }}>Quick actions</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <QuickAction to="/admin/books" icon={BookOpen} label="Manage books & editions" testId="qa-books" />
            <QuickAction to="/admin/media" icon={Image} label="Media library & uploads" testId="qa-media" />
            <QuickAction to="/admin/content/articles" icon={Newspaper} label="Articles & research posts" testId="qa-articles" />
            <QuickAction to="/admin/homepage" icon={FileText} label="Homepage sections" testId="qa-homepage" />
            {user.role === "owner" && <QuickAction to="/admin/staff" icon={Users2} label="Staff & roles" testId="qa-staff" />}
          </div>
        </section>

        <section>
          <h3 className="font-accent text-[10px] mb-4" style={{ color: "var(--gi-maroon)" }}>Recent activity</h3>
          <div className="border" style={{ borderColor: "var(--gi-border)", background: "#fff" }} data-testid="dashboard-activity">
            {activity.length === 0 ? (
              <div className="p-6 text-sm" style={{ color: "var(--gi-muted)" }}>No recorded activity yet.</div>
            ) : activity.map(a => (
              <div key={a.id} className="border-b last:border-b-0 px-5 py-3 text-sm flex items-start gap-3" style={{ borderColor: "var(--gi-divider)" }}>
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: "var(--gi-gold)" }} />
                <div className="flex-1">
                  <div style={{ color: "var(--gi-charcoal)" }}>
                    <strong>{a.actor_email}</strong> {a.action} <em>{a.entity_type}</em>
                    {a.entity_title ? ` — ${a.entity_title}` : ""}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--gi-muted)" }}>{new Date(a.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/admin/activity" className="btn-ghost mt-4" data-testid="qa-activity">View full history <ArrowRight size={12} /></Link>
        </section>
      </div>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, note }) => (
  <header className="mb-2">
    <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{eyebrow}</div>
    <h1 className="mt-2 font-heading text-4xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }}>{title}</h1>
    {note && <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>{note}</p>}
    <div className="hair-divider mt-6 max-w-xs" />
  </header>
);

const StatCard = ({ label, value, testId, tone }) => {
  const color = { maroon: "var(--gi-maroon)", gold: "#8A6A00", muted: "var(--gi-muted)" }[tone];
  return (
    <div className="p-6 border bg-white" style={{ borderColor: "var(--gi-border)" }} data-testid={testId}>
      <div className="font-accent text-[10px]" style={{ color }}>{label}</div>
      <div className="mt-3 font-heading text-5xl" style={{ color: "var(--gi-charcoal)" }}>{value}</div>
    </div>
  );
};

const QuickAction = ({ to, icon: Icon, label, testId }) => (
  <Link to={to} className="p-5 border bg-white flex items-center justify-between group hover:border-[var(--gi-maroon)] transition-colors" style={{ borderColor: "var(--gi-border)" }} data-testid={testId}>
    <div className="flex items-center gap-3">
      <Icon size={16} style={{ color: "var(--gi-maroon)" }} />
      <span className="text-sm font-medium" style={{ color: "var(--gi-charcoal)" }}>{label}</span>
    </div>
    <ArrowRight size={14} style={{ color: "var(--gi-maroon)" }} />
  </Link>
);
